import React, { type FC, useCallback, useState } from "react";
import PasswordlessForm from "@components/AuthFlow/Passwordless/PasswordlessForm";
import { trackClick, withPrefix } from "@tracking/tracking";
import { DEFAULT_SUBTITLE_COLOR } from "clientState/AuthFlow/providers/ModalScreenProvider/constants";
import {
  screenNames,
  useScreenState,
} from "clientState/AuthFlow/providers/ScreenProvider";
import { useTracking } from "@edreamsodigeo/frontend-core";
import { Body, Box, Flex } from "@edreamsodigeo/prisma-design-system";

import { FormLink } from "../FormLink";
import NewsletterOption from "../NewsletterOption";
import SocialAuth from "../SocialAuth";
import { ButtonText } from "./ButtonText";
import { type PasswordlessBodyProps } from "./contract";
import { SIGNUP_LINK_CLICK_LOGIN_ID } from "./datameshIds";

const { PASSWORDLESS, SIGNIN } = screenNames;

const PasswordlessBody: FC<PasswordlessBodyProps> = ({
  signinLink,
  showSigninLink,
  signupLink,
  showSignupLink,
  submitButtonLabel,
  onSuccess,
  emailLabelForm,
  isInitialScreen,
  isLoginSyncEnabled,
  renderSuccessScreen,
  renderSigninScreen,
  renderSignupScreen,
  renderLoginSyncScreen,
}) => {
  const { trackEvent } = useTracking();
  const { title: signupLinkTitle, linkLabel: signupLinkLabel } =
    signupLink || {};
  const { state } = useScreenState();

  const [email, setEmail] = useState(state.userData.email);
  const [acceptsNewsletter] = useState(state.userData.acceptsNewsletter);

  const onSignupClick = useCallback(() => {
    trackClick({ trackEvent, screen: SIGNIN, prefix: state.trackingPrefix });
    renderSignupScreen({ ...state.userData, email });
  }, [
    trackEvent,
    state.trackingPrefix,
    state.userData,
    renderSignupScreen,
    email,
  ]);

  return (
    <>
      <Box my="base">
        <PasswordlessForm
          email={email}
          setEmail={setEmail}
          acceptsNewsletter={acceptsNewsletter}
          submitButtonLabel={submitButtonLabel}
          emailLabelForm={emailLabelForm}
          isLoginSyncEnabled={isLoginSyncEnabled}
          renderSuccessScreen={renderSuccessScreen}
          renderLoginSyncScreen={renderLoginSyncScreen}
        />
      </Box>
      <Flex flexDirection="column" gap="base">
        {showSigninLink && signinLink && (
          <ButtonText
            trackingId={withPrefix(
              signinLink.trackingId || "",
              state.trackingPrefix,
            )}
            onClick={(): void =>
              renderSigninScreen({ ...state.userData, email })
            }
          >
            {signinLink.content}
          </ButtonText>
        )}
        {showSigninLink && (
          <SocialAuth
            google={{
              screen: PASSWORDLESS,
              closeScreen: onSuccess,
              trackingPrefix: state.trackingPrefix,
            }}
          />
        )}
        {showSignupLink && (
          <Flex justifyContent="center" alignItems="center" gap="2x_small">
            <Body
              color={DEFAULT_SUBTITLE_COLOR}
              type="xSmall"
              textAlign="center"
            >
              {signupLinkTitle}
            </Body>
            <FormLink
              withTextDecoration
              trackingId={withPrefix(
                SIGNUP_LINK_CLICK_LOGIN_ID,
                state.trackingPrefix,
              )}
              onClick={onSignupClick}
            >
              {signupLinkLabel}
            </FormLink>
          </Flex>
        )}

        <NewsletterOption shouldShow={isInitialScreen || !acceptsNewsletter} />
      </Flex>
    </>
  );
};

export default PasswordlessBody;
